import { BACKEND_ADDRESS } from '../utils/constants';

// CV Initialization and Management
export const initializeCV = async (userId, textInput, documentFile) => {
  const formData = new FormData();

  if (textInput && textInput.trim()) {
    formData.append('text_input', textInput);
  }

  if (documentFile) {
    formData.append('document', documentFile);
  }

  // Always send multipart FormData: the backend reads text_input/document as Form
  // fields, so a JSON body would be silently ignored (yields an empty CV).
  const response = await fetch(`${BACKEND_ADDRESS}/api/${userId}/initialize-cv`, {
    method: 'POST',
    body: formData
  });

  if (!response.ok) {
    throw new Error(`Failed to initialize CV: ${response.status}`);
  }

  return response.json();
};

// Process user input with AI
export const processInput = async (userId, userInput, currentQuestion, generatePdf = true) => {
  const response = await fetch(`${BACKEND_ADDRESS}/api/${userId}/process-input`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      userInput,
      currentQuestion,
      generate_pdf: generatePdf
    })
  });

  if (!response.ok) {
    throw new Error(`Failed to process input: ${response.status}`);
  }

  return response.json();
};

// PDF Management
export const fetchPDF = async (userId) => {
  const timestamp = new Date().getTime();
  const response = await fetch(`${BACKEND_ADDRESS}/api/${userId}/pdf/current?t=${timestamp}`);

  if (!response.ok) {
    throw new Error('Failed to fetch PDF');
  }

  return response.blob();
};

// Anquet (Form) Data Management
export const fetchAnquetData = async (userId) => {
  const response = await fetch(`${BACKEND_ADDRESS}/${userId}/anquet`);

  if (!response.ok) {
    throw new Error(`Failed to fetch anquet data: ${response.status}`);
  }

  return response.json();
};

export const saveAnquetData = async (userId, updatedData) => {
  const response = await fetch(`${BACKEND_ADDRESS}/api/${userId}/update-anquet`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(updatedData)
  });

  if (!response.ok) {
    throw new Error(`Failed to save anquet data: ${response.status}`);
  }

  return response.json();
};

// Taxonomy Data
export const loadTaxonomyData = async () => {
  const [
    languagesRes,
    proficiencyRes,
    educationRes,
    platformsRes,
    jobCategoriesRes,
    technicalSkillsRes,
    softSkillsRes,
    citiesRes,
    countriesRes
  ] = await Promise.all([
    fetch(`${BACKEND_ADDRESS}/api/taxonomy/languages`),
    fetch(`${BACKEND_ADDRESS}/api/taxonomy/proficiency-levels`),
    fetch(`${BACKEND_ADDRESS}/api/taxonomy/education-levels`),
    fetch(`${BACKEND_ADDRESS}/api/taxonomy/social-platforms`),
    fetch(`${BACKEND_ADDRESS}/api/taxonomy/job-categories`),
    fetch(`${BACKEND_ADDRESS}/api/taxonomy/technical-skills`),
    fetch(`${BACKEND_ADDRESS}/api/taxonomy/soft-skills`),
    fetch(`${BACKEND_ADDRESS}/api/taxonomy/cities`),
    fetch(`${BACKEND_ADDRESS}/api/taxonomy/countries`)
  ]);

  const responses = [
    languagesRes,
    proficiencyRes,
    educationRes,
    platformsRes,
    jobCategoriesRes,
    technicalSkillsRes,
    softSkillsRes,
    citiesRes,
    countriesRes
  ];

  for (const res of responses) {
    if (!res.ok) {
      throw new Error(`Failed to fetch taxonomy data: ${res.status}`);
    }
  }

  const [
    languagesData,
    proficiencyData,
    educationData,
    platformsData,
    jobCategoriesData,
    technicalSkillsData,
    softSkillsData,
    citiesData,
    countriesData
  ] = await Promise.all(responses.map(res => res.json()));

  return {
    languages: languagesData.languages || [],
    proficiencyLevels: proficiencyData.proficiency_levels || [],
    educationLevels: educationData.education_levels || [],
    socialPlatforms: platformsData.social_platforms || [],
    jobCategories: jobCategoriesData.job_categories || [],
    technicalSkills: technicalSkillsData.technical_skills || [],
    softSkills: softSkillsData.soft_skills || [],
    cities: citiesData.cities || [],
    countries: countriesData.countries || []
  };
};

// AI Field Improvement
export const improveField = async (userId, fieldName, currentText, experienceIndex = null) => {
  const payload = {
    field_name: fieldName,
    current_text: currentText,
    ...(experienceIndex !== null && { experience_index: experienceIndex })
  };

  const response = await fetch(`${BACKEND_ADDRESS}/api/${userId}/improve-field`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    throw new Error(`Failed to improve field: ${response.status}`);
  }

  return response.json();
};

// Export Functions
export const exportCV = async (userId) => {
  const response = await fetch(`${BACKEND_ADDRESS}/api/${userId}/export-cv`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' }
  });

  if (!response.ok) {
    throw new Error('Export failed');
  }

  return response.blob();
};

export const exportJSON = async (userId) => {
  const response = await fetch(`${BACKEND_ADDRESS}/api/${userId}/export-json`, {
    method: 'GET',
    headers: { 'Accept': 'application/json' }
  });

  if (!response.ok) {
    throw new Error('JSON export failed');
  }

  return response.blob();
};

// User Management
export const createNewUser = async () => {
  const response = await fetch(`${BACKEND_ADDRESS}/api/new-user`, { method: 'POST' });

  if (!response.ok) {
    throw new Error('Failed to create new user');
  }

  return response.json();
};
